// External Imports
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Offcanvas from 'react-bootstrap/Offcanvas';
import Row from 'react-bootstrap/Row';
import Table from 'react-bootstrap/Table';

import Moment from 'moment';

import * as formik from 'formik';
import * as yup from 'yup';

//Components
import ProcessingAlert from './ProcessingAlert';
import RequestingAlert from './alerts/RequestingAlert';

//Hooks
import { mailVouchers } from '../utils/quota';

//Main View
export default function Mailer({
  visible,
  closeFunction,
  subject,
  vouchers,
  bearerToken,
  customer_id,
}) {
  // Local Data
  const [error, setError] = useState({ message: '', severity: 'primary' });

  const schema = yup.object().shape({
    to: yup.string().required(),
    cc: yup.string(),
    subject: yup.string().required(),
  });
  const { Formik } = formik;

  // Event Handlers
  const handleForm = async (values) => {
    const codes = vouchers.map((voucher) => {
      return voucher.activation_code;
    });
    let postData = {
      to: values.to,
      subject: values.subject,
      activation_codes: codes,
    };

    if (values.cc != '') {
      postData['cc'] = values.cc;
    }

    await mailVouchers(bearerToken, postData, customer_id)
      .then((data) => {
        closeFunction(true);
      })
      .catch((error) => {
        setError({ message: 'Unable to send PDF! ' + error, severity: 'danger' });
      });
  };

  return (
    <Offcanvas show={visible} onHide={closeFunction} placement='bottom' className='h-50'>
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>Send Vouchers via E-Mail</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        {error.message != '' && (
          <Alert variant={error.severity}>
            <span>{error.message}</span>
          </Alert>
        )}

        <Row>
          <Col sm={4}>
            <Formik
              validationSchema={schema}
              onSubmit={handleForm}
              initialValues={{
                to: 'sales@rcs-southsudan.com' /* Admin users don't have e-mail field in JWT token.. */,
                cc: '',
                subject: subject,
              }}
              enableReinitialize
            >
              {({ handleSubmit, handleChange, isSubmitting, values, errors }) => (
                <Form noValidate onSubmit={handleSubmit}>
                  <Form.Group as={Row} className='mb-1' controlId='to'>
                    <Form.Label column sm={4}>
                      Send To
                    </Form.Label>
                    <Col sm={8}>
                      <Form.Control
                        type='text'
                        placeholder='user@domain.com'
                        name='to'
                        value={values.to}
                        onChange={handleChange}
                        isInvalid={!!errors.to}
                      />
                      <Form.Control.Feedback type='invalid'>{errors.to}</Form.Control.Feedback>
                    </Col>
                  </Form.Group>

                  <Form.Group as={Row} className='mb-1' controlId='cc'>
                    <Form.Label column sm={4}>
                      Copy
                    </Form.Label>
                    <Col sm={8}>
                      <Form.Control
                        type='text'
                        placeholder='user@domain.com'
                        name='cc'
                        value={values.cc}
                        onChange={handleChange}
                        isInvalid={!!errors.cc}
                      />
                      <Form.Control.Feedback type='invalid'>{errors.cc}</Form.Control.Feedback>
                    </Col>
                  </Form.Group>

                  <Form.Group as={Row} className='mb-1' controlId='subject'>
                    <Form.Label column sm={4}>
                      Subject
                    </Form.Label>
                    <Col sm={8}>
                      <Form.Control
                        type='text'
                        placeholder='Subject'
                        name='subject'
                        value={values.subject}
                        onChange={handleChange}
                        isInvalid={!!errors.subject}
                      />
                      <Form.Control.Feedback type='invalid'>{errors.subject}</Form.Control.Feedback>
                    </Col>
                  </Form.Group>

                  {isSubmitting ? (
                    <RequestingAlert />
                  ) : (
                    <Button variant='success' type='submit'>
                      Send
                    </Button>
                  )}
                </Form>
              )}
            </Formik>
          </Col>
          <Col sm={8}>
            <Table striped>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Activation Code</th>
                  <th>Status</th>
                  <th>Expiry Date</th>
                  <th>Quota</th>
                  <th>Validity</th>
                </tr>
              </thead>
              <tbody>
                {vouchers.length > 0 && (
                  <>
                    {vouchers.map((voucher) => (
                      <tr
                        key={voucher.activation_code}
                        className={
                          voucher.status == 'generated'
                            ? 'table-success'
                            : voucher.status == 'expired'
                              ? 'table-danger'
                              : voucher.status == 'used'
                                ? 'table-dark'
                                : 'table-warning'
                        }
                      >
                        <td>
                          {voucher.type == 'reset' && <span>Reset Token</span>}
                          {voucher.type == 'quota' && <span>Volume Voucher</span>}
                          {voucher.type == 'topup' && <span>Topup Token</span>}
                        </td>
                        <td>{voucher.activation_code}</td>
                        <td>
                          {voucher.status == 'generated' && <span>Available</span>}
                          {voucher.status == 'queued' && (
                            <span>Allocated to {voucher.customer_id}</span>
                          )}
                          {voucher.status == 'used' && <span>Used</span>}
                        </td>
                        <td>{Moment(voucher.expiry_date).format('YYYY-MM-DD')}</td>
                        <td>{voucher.quota_gb && <span>{voucher.quota_gb} GB</span>}</td>
                        <td>
                          {voucher.validity_days && <span>{voucher.validity_days} days</span>}
                        </td>
                      </tr>
                    ))}
                  </>
                )}
              </tbody>
            </Table>
          </Col>
        </Row>
      </Offcanvas.Body>
    </Offcanvas>
  );
}

Mailer.propTypes = {
  visible: PropTypes.bool,
  closeFunction: PropTypes.func,
  subject: PropTypes.string,
  vouchers: PropTypes.array,
  bearerToken: PropTypes.string,
  customer_id: PropTypes.string,
};
